import axios from "axios";
import { apiURI } from "../ENVs";

/**
 * Streams a server-generated CSV export via the dedicated `POST /form/export`
 * endpoint, then saves it as a file download.
 *
 * The column list + filters travel in the request BODY, not the URL query — a
 * large structured query string is what the Front Door WAF rejects. The JWT /
 * subscription-key headers are still added by the axios interceptor, so the
 * request clears the gateway. The response is streamed as a blob (survives
 * in-app navigation, never blocks the UI) and gzipped on the wire by the backend
 * compression middleware.
 *
 * @param {Object}  args
 * @param {string}  args.list      which list to export (e.g. "requests"); maps
 *   to a backend export-registry entry.
 * @param {Array}   args.columns   ordered [{ field, header, format }] — the
 *   frontend-owned column list (see buildCsvColumns).
 * @param {Object}  [args.search]  active per-column search ({ field: value }).
 * @param {Object}  [args.sort]    active sort ({ field, dir }).
 * @param {string}  args.filename  name the saved file gets (a blob: URL has no
 *   filename, so it must be set explicitly on the anchor).
 * @throws re-throws the axios error (with a decoded `.message` when the backend
 *   returned a JSON error blob) so the caller can surface it.
 */
export async function downloadCsvViaExport({
  list,
  columns,
  search,
  sort,
  filename,
}) {
  try {
    const { data } = await axios.post(
      `${apiURI}/form/export`,
      {
        list,
        columns,
        // Only include search / sort when active.
        ...(search && Object.keys(search).length ? { search } : {}),
        ...(sort ? { sort } : {}),
      },
      { responseType: "blob" }
    );

    const objectUrl = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke after a tick so the browser has started reading the blob for the
    // download (revoking synchronously can cancel the download in some browsers).
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch (error) {
    // An error response also arrives as a blob (responseType:"blob"); decode it
    // to a readable message so callers don't surface "[object Blob]".
    if (error?.response?.data instanceof Blob) {
      try {
        const asText = await error.response.data.text();
        const parsed = JSON.parse(asText);
        error.message = parsed?.error || asText || error.message;
      } catch {
        // Leave the original error message if the blob isn't JSON.
      }
    }
    throw error;
  }
}
